import React from 'react';
import {
  getLaneState,
  schedulerTheme,
} from 'pos-client/components/customScheduler/theme/schedulerTheme.ts';
import type {
  Appointment,
  AppointmentComponentProps,
  CellData,
} from 'pos-client/components/customScheduler/types/schedulerTypes';

import OverlapBubble from './OverlapBubble.tsx';
import ScheduleAppointment from './ScheduleAppointment.tsx';

import styles from './ScheduleLane.module.css';

interface ScheduleLaneProps {
  laneId: string;
  timeSlots: Date[];
  appointments: Appointment[];
  cellHeightPx: number;
  cellDuration: number;
  conflictLanes: string[];
  defaultLanes: string[];
  tradingTimes?: {
    openingTime: Date;
    closingTime: Date;
    openingMinutes?: number;
    closingMinutes?: number;
  };
  validatedConfig: {
    groups: string[];
    cellDuration: number;
  };
  appointmentLayouts: Map<
    string,
    Map<
      string | number,
      { column: number; totalColumns: number; hidden: boolean }
    >
  >;
  isBeforeCurrentTime: (slot: Date) => boolean;
  visibleRowStart: number;
  visibleRowEnd: number;
  currentTimePosition: number | null;
  currentTimeColor: string;
  onCellContextMenu?: (data: {
    cellData: unknown;
    cellElement: HTMLDivElement;
    event: MouseEvent;
    action: string;
  }) => void;
  onAppointmentClick?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
  }) => void;
  onAppointmentDblClick?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
  }) => void;
  onAppointmentContextMenu?: (data: {
    appointmentData: Appointment;
    appointmentElement: HTMLDivElement;
    event: MouseEvent;
  }) => void;
  onAppointmentTooltipShowing?: (data: {
    appointmentData: Appointment;
    cancel: boolean;
  }) => void;
  onAppointmentDragStart: (
    e: React.DragEvent<HTMLDivElement>,
    appointment: Appointment
  ) => void;
  onAppointmentDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onAppointmentDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onAppointmentUpdating?: (data: {
    appointmentData: Appointment;
    newData: Partial<Appointment>;
    cancel: boolean;
  }) => void;
  onAppointmentUpdated?: (data: { appointmentData: Appointment }) => void;
  handleCellDrop: (
    e: React.DragEvent<HTMLDivElement>,
    cellData: unknown,
    laneId: string,
    slot: Date
  ) => void;
  handleAppointmentDelete: (appointment: Appointment) => void;
  AppointmentComponent?: React.ComponentType<{
    data: Appointment;
    index: number;
    userIsDragging: boolean;
  }>;
  dataCellTemplate?: (
    cellData: CellData,
    index: number,
    cellElement: HTMLDivElement
  ) => void;
  formatSlotTime: (date: Date) => string;
  justResized: string | number | null;
  setContextMenu: (menu: {
    visible: boolean;
    x: number;
    y: number;
    items: Array<{ id: string; label: string; action: () => void }>;
  }) => void;
  t: (key: string) => string;
  setJobDetailsModalData: (data: Record<string, unknown>) => void;
  setSelectedOrderDetailsForAssignTechnician: (data: {
    orderId: string;
    jobId: string;
  }) => void;
  canAuthorizeBlockedSlot: boolean;
  canAuthorizeOwnBlockedSlot: boolean;
  currentUserId?: string;
  unauthorizeBlockedSlot: (data: { blockedSlotId: string }) => void;
  authorizeBlockedSlot: (data: { blockedSlotId: string }) => void;
  userIsDragging: boolean;
}

const ScheduleLane: React.FC<ScheduleLaneProps> = ({
  laneId,
  timeSlots,
  appointments,
  cellHeightPx,
  cellDuration,
  conflictLanes,
  defaultLanes,
  tradingTimes,
  validatedConfig,
  appointmentLayouts,
  isBeforeCurrentTime,
  visibleRowStart,
  visibleRowEnd,
  currentTimePosition,
  currentTimeColor,
  onCellContextMenu,
  onAppointmentClick,
  onAppointmentDblClick,
  onAppointmentContextMenu,
  onAppointmentTooltipShowing,
  onAppointmentDragStart,
  onAppointmentDragOver,
  onAppointmentDragEnd,
  onAppointmentUpdating,
  onAppointmentUpdated,
  handleCellDrop,
  handleAppointmentDelete,
  AppointmentComponent,
  dataCellTemplate,
  formatSlotTime,
  justResized,
  setContextMenu,
  t,
  setJobDetailsModalData,
  setSelectedOrderDetailsForAssignTechnician,
  canAuthorizeBlockedSlot,
  canAuthorizeOwnBlockedSlot,
  currentUserId,
  unauthorizeBlockedSlot,
  authorizeBlockedSlot,
  userIsDragging,
}) => {
  const laneRef = React.useRef<HTMLDivElement | null>(null);

  const hasConflict = conflictLanes.includes(laneId);
  const isDefaultLane = defaultLanes.includes(laneId);
  const laneState = getLaneState(hasConflict, isDefaultLane);
  const laneColors = schedulerTheme.laneStates[laneState];

  const totalSlots = timeSlots.length;
  const laneHeightPx = totalSlots * cellHeightPx;

  const laneStart = timeSlots[0];
  const cellDurationMs = validatedConfig.cellDuration * 60 * 1000;

  const minuteOfDayToLanePx = (minuteOfDay: number | undefined) => {
    if (minuteOfDay === undefined || !laneStart) return null;
    const laneStartMinutes =
      laneStart.getHours() * 60 + laneStart.getMinutes();
    const minutesFromLaneStart = minuteOfDay - laneStartMinutes;
    if (minutesFromLaneStart < 0) return null;
    const px =
      (minutesFromLaneStart / validatedConfig.cellDuration) * cellHeightPx;
    if (px > laneHeightPx) return null;
    return px;
  };

  const openingLinePx = minuteOfDayToLanePx(tradingTimes?.openingMinutes);
  const closingLinePx = minuteOfDayToLanePx(tradingTimes?.closingMinutes);

  const slotIndexFromClientY = React.useCallback(
    (clientY: number): number | null => {
      const lane = laneRef.current;
      if (!lane) return null;
      const laneTop = lane.getBoundingClientRect().top;
      const idx = Math.floor((clientY - laneTop) / cellHeightPx);
      if (idx < 0 || idx >= totalSlots) return null;
      return idx;
    },
    [cellHeightPx, totalSlots]
  );

  const buildCellData = React.useCallback(
    (slot: Date) => ({
      startDate: slot,
      groupIndex: 0,
      groups: { [validatedConfig.groups[0] || 'laneId']: laneId },
    }),
    [laneId, validatedConfig.groups]
  );

  const handleLaneContextMenu = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const idx = slotIndexFromClientY(e.clientY);
      if (idx === null) return;
      const slot = timeSlots[idx];
      if (isBeforeCurrentTime(slot)) return;

      e.preventDefault();

      const cellData = buildCellData(slot);
      const cellElement = e.currentTarget as HTMLDivElement;

      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        items: [
          {
            id: 'add-order',
            label: t('orders.addNewOrder'),
            action: () => {
              onCellContextMenu?.({
                cellData,
                cellElement,
                event: e.nativeEvent as MouseEvent,
                action: 'add-order',
              });
            },
          },
          {
            id: 'add-blocked-slot',
            label: t('scheduling.blockedSlot.addBlockedSlot'),
            action: () => {
              onCellContextMenu?.({
                cellData,
                cellElement,
                event: e.nativeEvent as MouseEvent,
                action: 'add-blocked-slot',
              });
            },
          },
          {
            id: 'add-job',
            label: t('jobs.addNewJob'),
            action: () => {
              onCellContextMenu?.({
                cellData,
                cellElement,
                event: e.nativeEvent as MouseEvent,
                action: 'add-job',
              });
            },
          },
        ],
      });
    },
    [
      buildCellData,
      isBeforeCurrentTime,
      onCellContextMenu,
      setContextMenu,
      slotIndexFromClientY,
      t,
      timeSlots,
    ]
  );

  const handleLaneDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const index = slotIndexFromClientY(e.clientY);
      if (index === null) return;
      const slot = timeSlots[index];
      const cellData = buildCellData(slot);
      handleCellDrop(e, cellData, laneId, slot);
    },
    [buildCellData, handleCellDrop, laneId, slotIndexFromClientY, timeSlots]
  );

  const cellStart = Math.max(0, visibleRowStart);
  const cellEnd = Math.min(totalSlots, visibleRowEnd);

  const appointmentEntries: Array<{
    appointment: Appointment;
    key: string;
    topPx: number;
    heightPx: number;
    layout?: {
      column: number;
      totalColumns: number;
      hidden: boolean;
    };
  }> = [];
  const hiddenAppointmentsBySlotIndex = new Map<number, Appointment[]>();

  for (let i = 0; i < appointments.length; i++) {
    const appointment = appointments[i];
    const appStartVal =
      typeof appointment.startDateDisplay === 'string'
        ? appointment.startDateDisplay
        : appointment.startDate;
    const appEndVal =
      typeof appointment.endDateDisplay === 'string'
        ? appointment.endDateDisplay
        : appointment.endDate;
    const appStart = new Date(appStartVal);
    const appEnd = new Date(appEndVal);

    const startMinutesFromLane =
      (appStart.getTime() - laneStart.getTime()) / (1000 * 60);
    const topPx =
      (startMinutesFromLane / validatedConfig.cellDuration) * cellHeightPx;

    const durationMs = appEnd.getTime() - appStart.getTime();
    const spannedCells = Math.ceil(durationMs / cellDurationMs);
    const heightPx = spannedCells * cellHeightPx;

    const appointmentId = String(
      appointment.jobId ||
        appointment.scheduleId ||
        appointment.bookingReference ||
        `${appointment.startDate}-${appointment.endDate}-${appointment.text}`
    );
    const appointmentKey = `${laneId}-${appointmentId}`;
    const slotLayouts = appointmentLayouts.get(appointmentKey);
    const layout = slotLayouts?.get(appointmentId);

    if (layout?.hidden) {
      const anchorSlotIndex = Math.floor(
        startMinutesFromLane / validatedConfig.cellDuration
      );
      const bucket =
        hiddenAppointmentsBySlotIndex.get(anchorSlotIndex) ?? [];
      bucket.push(appointment);
      hiddenAppointmentsBySlotIndex.set(anchorSlotIndex, bucket);
      continue;
    }

    appointmentEntries.push({
      appointment,
      key: `${laneId}-app-${i}`,
      topPx,
      heightPx,
      layout,
    });
  }

  return (
    <div
      ref={laneRef}
      className={styles.lane}
      data-lane-state={laneState}
      style={{
        position: 'relative',
        flex: '1 1 0',
        minWidth: '300px',
        height: `${laneHeightPx}px`,
        backgroundColor: laneColors.background,
      }}
      onContextMenu={handleLaneContextMenu}
      onDragOver={onAppointmentDragOver}
      onDrop={handleLaneDrop}
    >
      {Array.from({ length: Math.max(0, cellEnd - cellStart) }, (_, offset) => {
        const slotIndex = cellStart + offset;
        const slot = timeSlots[slotIndex];
        const cellData = buildCellData(slot);

        const shouldUseInlineBackground = laneState === 'normal';
        const backgroundColor = laneColors.background;

        const cellTop = slotIndex * cellHeightPx;
        const cellBottom = cellTop + cellHeightPx;
        const showCurrentTime =
          currentTimePosition !== null &&
          currentTimePosition >= cellTop &&
          currentTimePosition < cellBottom;

        return (
          <div
            key={`${laneId}-${slotIndex}`}
            ref={(el) => {
              if (el && dataCellTemplate) {
                dataCellTemplate(cellData, slotIndex, el);
              }
            }}
            className={styles.cell}
            data-lane-state={laneState}
            style={{
              position: 'absolute',
              top: `${cellTop}px`,
              left: 0,
              right: 0,
              height: `${cellHeightPx}px`,
              borderTop: 'none',
              ...(shouldUseInlineBackground && { backgroundColor }),
            }}
          >
            {showCurrentTime && (
              <div
                style={{
                  position: 'absolute',
                  top: `${currentTimePosition! - cellTop}px`,
                  left: -3,
                  right: 0,
                  height: '2px',
                  backgroundColor: currentTimeColor,
                  pointerEvents: 'none',
                  zIndex: 24,
                }}
              />
            )}
          </div>
        );
      })}

      {appointmentEntries.map(({ appointment, key, topPx, heightPx, layout }) => (
        <ScheduleAppointment
          key={key}
          appointment={appointment}
          appointmentHeightPx={heightPx}
          offsetPx={topPx}
          layout={layout}
          cellHeightPx={cellHeightPx}
          cellDuration={cellDuration}
          validatedConfig={validatedConfig}
          justResized={justResized}
          onAppointmentClick={onAppointmentClick}
          onAppointmentDblClick={onAppointmentDblClick}
          onAppointmentContextMenu={onAppointmentContextMenu}
          onAppointmentTooltipShowing={onAppointmentTooltipShowing}
          onAppointmentDragStart={onAppointmentDragStart}
          onAppointmentDragOver={onAppointmentDragOver}
          onAppointmentDragEnd={onAppointmentDragEnd}
          onAppointmentUpdating={onAppointmentUpdating}
          onAppointmentUpdated={onAppointmentUpdated}
          handleAppointmentDelete={handleAppointmentDelete}
          AppointmentComponent={AppointmentComponent}
          formatSlotTime={formatSlotTime}
          setContextMenu={setContextMenu}
          t={t}
          setJobDetailsModalData={setJobDetailsModalData}
          setSelectedOrderDetailsForAssignTechnician={
            setSelectedOrderDetailsForAssignTechnician
          }
          canAuthorizeBlockedSlot={canAuthorizeBlockedSlot}
          canAuthorizeOwnBlockedSlot={canAuthorizeOwnBlockedSlot}
          currentUserId={currentUserId}
          unauthorizeBlockedSlot={unauthorizeBlockedSlot}
          authorizeBlockedSlot={authorizeBlockedSlot}
          userIsDragging={userIsDragging}
        />
      ))}

      {Array.from(hiddenAppointmentsBySlotIndex.entries()).map(
        ([slotIndex, hidden]) => (
          <div
            key={`${laneId}-overlap-${slotIndex}`}
            style={{
              position: 'absolute',
              top: `${slotIndex * cellHeightPx}px`,
              left: 0,
              right: 0,
              height: `${cellHeightPx}px`,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', height: '100%' }}>
              <OverlapBubble
                hiddenCount={hidden.length}
                hiddenAppointments={hidden}
                appointmentComponent={
                  AppointmentComponent as
                    | React.ComponentType<AppointmentComponentProps>
                    | undefined
                }
                onAppointmentClick={onAppointmentClick}
                onAppointmentContextMenu={onAppointmentContextMenu}
              />
            </div>
          </div>
        )
      )}
      {openingLinePx !== null && openingLinePx > 0 && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: `${openingLinePx}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}
      {closingLinePx !== null && closingLinePx < laneHeightPx && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: `${closingLinePx}px`,
            left: 0,
            right: 0,
            height: `${laneHeightPx - closingLinePx}px`,
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />
      )}
      {openingLinePx !== null && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: `${openingLinePx}px`,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: schedulerTheme.ui.tradingTimeOpen,
            pointerEvents: 'none',
            zIndex: 22,
          }}
        />
      )}
      {closingLinePx !== null && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: `${closingLinePx}px`,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: schedulerTheme.ui.tradingTimeClose,
            pointerEvents: 'none',
            zIndex: 22,
          }}
        />
      )}
    </div>
  );
};

export default React.memo(ScheduleLane);
